import { useEffect, useRef, useState } from "react";
import "./FramePhotoEditModal.css";

const clampScale = (value) => Math.min(Math.max(value, 1), 4);

const getImageDrawSize = (image, box, scale) => {
  const imgRatio = image.width / image.height;
  const boxRatio = box.w / box.h;

  let width;
  let height;

  if (imgRatio > boxRatio) {
    height = box.h;
    width = height * imgRatio;
  } else {
    width = box.w;
    height = width / imgRatio;
  }

  return {
    width: width * scale,
    height: height * scale,
  };
};

const clampPosition = (x, y, drawWidth, drawHeight, box) => {
  const limitX = Math.max(0, (drawWidth - box.w) / 2);
  const limitY = Math.max(0, (drawHeight - box.h) / 2);

  return {
    x: Math.min(Math.max(x, -limitX), limitX),
    y: Math.min(Math.max(y, -limitY), limitY),
  };
};

export default function FramePhotoEditModal({
  box,
  slot,
  title = "Ajustar foto",
  onClose,
  onConfirm,
  onChangeImage,
  compactOnMobile = false,
}) {
  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const lastTouchTimeRef = useRef(0);

  const [position, setPosition] = useState(slot?.position || { x: 0, y: 0 });
  const [scale, setScale] = useState(slot?.scale || 1);

  const image = slot?.image;

  useEffect(() => {
    setPosition(slot?.position || { x: 0, y: 0 });
    setScale(slot?.scale || 1);
  }, [slot?.image, slot?.position, slot?.scale]);

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;

    return {
      x: ((clientX - rect.left) / rect.width) * box.w,
      y: ((clientY - rect.top) / rect.height) * box.h,
      clientX,
      clientY,
    };
  };

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image || !box) return;

    const ctx = canvas.getContext("2d");
    const drawSize = getImageDrawSize(image, box, scale);
    const limitedPosition = clampPosition(
      position.x,
      position.y,
      drawSize.width,
      drawSize.height,
      box,
    );

    canvas.width = box.w;
    canvas.height = box.h;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f5f2ed";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      (box.w - drawSize.width) / 2 + limitedPosition.x,
      (box.h - drawSize.height) / 2 + limitedPosition.y,
      drawSize.width,
      drawSize.height,
    );
  }, [box, image, position, scale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const wheelHandler = (event) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.12 : 0.12;
      setScale((current) => clampScale(current + delta));
    };

    canvas.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      canvas.removeEventListener("wheel", wheelHandler);
    };
  }, [image]);

  if (!box || !image) return null;

  const startMove = (event) => {
    if (event.type.startsWith("touch")) {
      lastTouchTimeRef.current = Date.now();
    }

    if (
      event.type.startsWith("mouse") &&
      Date.now() - lastTouchTimeRef.current < 700
    ) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.touches?.length === 2) {
      isDraggingRef.current = false;
      pinchDistanceRef.current = getTouchDistance(event.touches);
      pinchStartScaleRef.current = scale;
      return;
    }

    const point = getCanvasPoint(event);
    isDraggingRef.current = true;
    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const movePhoto = (event) => {
    if (event.type.startsWith("touch")) {
      lastTouchTimeRef.current = Date.now();
    }

    if (
      event.type.startsWith("mouse") &&
      Date.now() - lastTouchTimeRef.current < 700
    ) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.touches?.length === 2 && pinchDistanceRef.current) {
      const currentDistance = getTouchDistance(event.touches);
      const zoomFactor = currentDistance / pinchDistanceRef.current;
      setScale(clampScale(pinchStartScaleRef.current * zoomFactor));
      return;
    }

    if (!isDraggingRef.current) return;

    const point = getCanvasPoint(event);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = box.w / rect.width;
    const scaleY = box.h / rect.height;
    const dx = (point.clientX - lastPointRef.current.x) * scaleX;
    const dy = (point.clientY - lastPointRef.current.y) * scaleY;

    setPosition((current) => {
      const drawSize = getImageDrawSize(image, box, scale);
      return clampPosition(
        current.x + dx,
        current.y + dy,
        drawSize.width,
        drawSize.height,
        box,
      );
    });

    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const stopMove = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
  };

  const stopTouchMove = () => {
    lastTouchTimeRef.current = Date.now();
    stopMove();
  };

  const handleChangeImage = (event) => {
    const file = event.target.files?.[0];
    if (!file || !onChangeImage) return;

    onChangeImage(file);
    event.target.value = "";
  };

  const handleConfirm = () => {
    const drawSize = getImageDrawSize(image, box, scale);

    onConfirm({
      position: clampPosition(
        position.x,
        position.y,
        drawSize.width,
        drawSize.height,
        box,
      ),
      scale,
    });
  };

  return (
    <div
      className={
        compactOnMobile
          ? "frame-photo-modal frame-photo-modal--compact-mobile"
          : "frame-photo-modal"
      }
      role="dialog"
      aria-modal="true"
    >
      <div className="frame-photo-modal__panel">
        <div className="frame-photo-modal__header">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            X
          </button>
        </div>

        <p className="frame-photo-modal__hint">
          Mueve la foto y amplia hasta encajarla. Pulsa Listo para aplicar.
        </p>

        <canvas
          ref={canvasRef}
          aria-label="Editar encuadre de la foto"
          onMouseDown={startMove}
          onMouseMove={movePhoto}
          onMouseUp={stopMove}
          onMouseLeave={stopMove}
          onTouchStart={startMove}
          onTouchMove={movePhoto}
          onTouchEnd={stopTouchMove}
          onTouchCancel={stopTouchMove}
        />

        <div className="frame-photo-modal__actions">
          {onChangeImage && (
            <label className="frame-photo-modal__change">
              Cambiar foto
              <input
                type="file"
                accept="image/*"
                onChange={handleChangeImage}
              />
            </label>
          )}

          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" onClick={handleConfirm}>
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}




